import type { FC, ReactNode } from 'react'

import { Sparkles } from 'lucide-react'

import { cn } from '@/lib/utils'
import { getHubTheme } from '@/constants/hubs'

interface AnswerBlockProps {
    answer: string | null
    hub: string
    /**
     * The row this answer belongs to, so the block stays inline-editable.
     * `table` is 'guides' or 'posts'; `id` is that row's surrogate id.
     */
    table: 'guides' | 'posts'
    rowId: string | number
    className?: string
}

/**
 * The short, self-contained answer to the page's core question, placed above
 * everything else. It exists to be lifted verbatim by an answer engine, which
 * is why it is stored as its own column rather than derived from the standfirst.
 */
export const AnswerBlock: FC<AnswerBlockProps> = ({
    answer,
    hub,
    table,
    rowId,
    className
}): ReactNode => {
    if (!answer || answer.trim().length === 0) return null

    const theme = getHubTheme(hub)

    return (
        <section
            aria-labelledby='the-short-answer'
            className={cn('rounded-xl border-l-4 bg-card p-5 shadow-sm sm:p-6', theme.borderAccent, className)}
        >
            <h2
                id='the-short-answer'
                className='flex items-center gap-2 font-heading text-xs font-semibold uppercase tracking-wide text-muted-foreground'
            >
                <Sparkles className={cn('size-4', theme.text)} aria-hidden='true' />
                The short answer
            </h2>

            <p
                className='mt-3 text-lg leading-relaxed text-foreground'
                data-mdlfy-cms={`${table}:${rowId}:answer`}
            >
                {answer}
            </p>
        </section>
    )
}
